import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { DEFAULT_PAGE_SIZE_OPTIONS } from "@/shared/hooks/useClientPagination";

function pageButtons(page, pageCount) {
  if (pageCount <= 5) return Array.from({ length: pageCount }, (_, index) => index + 1);
  const middle = [page - 1, page, page + 1].filter((value) => value > 1 && value < pageCount);
  return [1, ...(middle[0] > 2 ? ["start-gap"] : []), ...middle, ...(middle[middle.length - 1] < pageCount - 1 ? ["end-gap"] : []), pageCount];
}

/** Accessible, responsive pagination for client-side collections. */
export default function Pagination({
  page,
  pageSize,
  pageCount,
  totalItems,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  label = "Results",
  testId = "pagination",
}) {
  if (!totalItems) return null;
  const first = (page - 1) * pageSize + 1;
  const last = Math.min(page * pageSize, totalItems);

  return (
    <nav aria-label={`${label} pagination`} className="flex flex-col gap-3 border-t border-line pt-4 sm:flex-row sm:items-center sm:justify-between" data-testid={testId}>
      <div className="flex items-center gap-2 text-sm text-ink-secondary">
        <span data-testid={`${testId}-summary`}>Showing {first}–{last} of {totalItems}</span>
        <label className="ml-auto flex items-center gap-2 sm:ml-0">
          <span className="sr-only">Items per page</span>
          <select value={pageSize} onChange={(event) => onPageSizeChange(Number(event.target.value))} className="input-field !h-10 !w-auto !py-1 text-sm" data-testid={`${testId}-page-size`}>
            {pageSizeOptions.map((size) => <option key={size} value={size}>{size} / page</option>)}
          </select>
        </label>
      </div>

      <div className="flex items-center justify-between gap-1 sm:justify-end" role="list">
        <button type="button" onClick={() => onPageChange(page - 1)} disabled={page === 1} className="btn-ghost min-h-[44px] min-w-[44px] !p-2 disabled:opacity-40" aria-label="Previous page" data-testid={`${testId}-previous`}><ChevronLeft className="h-4 w-4" /></button>
        {pageButtons(page, pageCount).map((entry) => entry === "start-gap" || entry === "end-gap" ? <span key={entry} className="min-w-[28px] text-center text-ink-muted" aria-hidden="true">…</span> : <button type="button" key={entry} onClick={() => onPageChange(entry)} aria-current={entry === page ? "page" : undefined} className={`min-h-[44px] min-w-[44px] rounded-lg px-2 text-sm ${entry === page ? "bg-brand text-white" : "text-ink-secondary hover:bg-bg-elevated"}`} data-testid={`${testId}-page-${entry}`}>{entry}</button>)}
        <button type="button" onClick={() => onPageChange(page + 1)} disabled={page === pageCount} className="btn-ghost min-h-[44px] min-w-[44px] !p-2 disabled:opacity-40" aria-label="Next page" data-testid={`${testId}-next`}><ChevronRight className="h-4 w-4" /></button>
      </div>
    </nav>
  );
}
